import * as React from "react";
import { Dialog } from "primereact/dialog";
import { DataTable, DataTablePageEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputTextarea } from "primereact/inputtextarea";
import { ConfirmDialog } from "primereact/confirmdialog";
import { useTableNameFromUrl } from "../hooks/useTableNameFromUrl";
import { PossibleColumnProps, RowFromView, ViewNames } from "../types";
import { Delivery } from "../types/delivery.ts";
import { api, Comment } from "../api";
import { useShowToast } from "../contexts/ToastContext.tsx";
import { extractApiError } from "../api/errors.ts";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { SerializedError } from "@reduxjs/toolkit";

type CommentsDialogProps<TView extends ViewNames> = {
  rowData: RowFromView<TView> | null;
  onHide: () => void;
  columns: PossibleColumnProps<RowFromView<TView>>[];
};

const COMMENTS_PER_PAGE = 10;

export function CommentsDialog<TView extends ViewNames>({
  rowData,
  onHide,
}: CommentsDialogProps<TView>) {
  const tableName = useTableNameFromUrl();
  const showToast = useShowToast();
  // Helper function to check if row is a Delivery
  const isDeliveryRow = (row: unknown): row is Delivery =>
    typeof row === "object" &&
    row !== null &&
    "source" in row &&
    "objectId" in row &&
    (row.source === "TransportOrder" || row.source === "ForwardingOrder");

  // Handle special case for deliveries
  const actualTableName = React.useMemo<Comment["table"] | null>(() => {
    if (tableName === null && isDeliveryRow(rowData)) {
      return rowData.source;
    }
    // Filter out table names that are not supported by comments
    const validTables: Comment["table"][] = [
      "Company",
      "Contract",
      "Driver",
      "ForwardingOrder",
      "Location",
      "Product",
      "TransportOrder",
    ];
    if (tableName && validTables.includes(tableName as Comment["table"])) {
      return tableName as Comment["table"];
    }
    return null;
  }, [tableName, rowData]);

  const actualParentId = React.useMemo(() => {
    if (isDeliveryRow(rowData)) {
      return rowData.objectId;
    }
    return rowData?.id ?? 0;
  }, [rowData]);

  // State management
  const [commentsPage, setCommentsPage] = React.useState(0);
  const [newCommentText, setNewCommentText] = React.useState("");
  const [editingCommentId, setEditingCommentId] = React.useState<number | null>(
    null,
  );
  const [deleteConfirmVisible, setDeleteConfirmVisible] = React.useState(false);
  const [commentToDelete, setCommentToDelete] = React.useState<Comment | null>(
    null,
  );

  // RTK Query hooks
  const { data: comments, isFetching } =
    api.endpoints.listCommentsApiCommentsGet.useQuery(
      {
        table: actualTableName ?? undefined,
        parentId: actualParentId,
        orderBy: "-created_at",
        limit: COMMENTS_PER_PAGE,
        offset: commentsPage * COMMENTS_PER_PAGE,
      },
      { skip: !actualTableName },
    );

  const [createComment, { isLoading: isCreating }] =
    api.endpoints.createCommentApiCommentsPost.useMutation();
  const [updateComment, { isLoading: isUpdating }] =
    api.endpoints.updateCommentApiCommentsCommentIdPut.useMutation();
  const [deleteComment] =
    api.endpoints.deleteCommentApiCommentsCommentIdDelete.useMutation();

  // Pagination handler
  const onPage = React.useCallback((e: DataTablePageEvent) => {
    setCommentsPage(e.page ?? 0);
  }, []);

  // Create comment handler
  const handleCreateComment = React.useCallback(async () => {
    if (!newCommentText.trim() || !actualTableName) return;

    const response = await createComment({
      commentCreate: {
        text: newCommentText.trim(),
        parent_id: actualParentId,
        table: actualTableName,
      },
    });

    if (response && "error" in response) {
      console.error("Failed to create comment:", response.error);
      showToast({
        severity: "error",
        summary: "Błąd",
        detail: extractApiError(
          response.error as FetchBaseQueryError | SerializedError,
        ),
      });
    } else {
      setNewCommentText("");
      setCommentsPage(0);
    }
  }, [
    newCommentText,
    actualTableName,
    actualParentId,
    createComment,
    showToast,
  ]);

  // Edit comment handlers
  const handleStartEdit = React.useCallback((comment: Comment) => {
    setEditingCommentId(comment.id);
    setNewCommentText(comment.text);
  }, []);

  const handleCancelEdit = React.useCallback(() => {
    setEditingCommentId(null);
    setNewCommentText("");
  }, []);

  const handleSaveEdit = React.useCallback(
    async (commentId: number) => {
      if (!newCommentText.trim()) return;

      try {
        await updateComment({
          commentId,
          commentUpdate: {
            text: newCommentText.trim(),
          },
        }).unwrap();

        setEditingCommentId(null);
        setNewCommentText("");
      } catch (error) {
        console.error("Failed to update comment:", error);
      }
    },
    [newCommentText, updateComment],
  );

  // Delete comment handlers
  const handleConfirmDelete = React.useCallback((comment: Comment) => {
    setCommentToDelete(comment);
    setDeleteConfirmVisible(true);
  }, []);

  const handleDeleteComment = React.useCallback(async () => {
    if (!commentToDelete) return;

    try {
      await deleteComment({
        commentId: commentToDelete.id,
      }).unwrap();

      setDeleteConfirmVisible(false);
      setCommentToDelete(null);
    } catch (error) {
      console.error("Failed to delete comment:", error);
      setDeleteConfirmVisible(false);
      setCommentToDelete(null);
    }
  }, [commentToDelete, deleteComment]);

  // Dialog hide handler - reset all state
  const handleHide = React.useCallback(() => {
    onHide();
    setCommentsPage(0);
    setNewCommentText("");
    setEditingCommentId(null);
    setDeleteConfirmVisible(false);
    setCommentToDelete(null);
  }, [onHide]);

  // Column body renderers
  const createdAtBody = (comment: Comment) => {
    return new Date(comment.created_at).toLocaleString();
  };

  const createdByBody = (comment: Comment) => {
    return comment.created_by.email;
  };

  const textBody = (comment: Comment) => {
    return <div style={{ whiteSpace: "pre-wrap" }}>{comment.text}</div>;
  };

  const updatedAtBody = (comment: Comment) => {
    if (comment.updated_at && comment.updated_by) {
      return `${new Date(comment.updated_at).toLocaleString()} przez ${comment.updated_by.email}`;
    }
    return "-";
  };

  const actionsBody = (comment: Comment) => {
    if (!comment.editable) return null;

    return (
      <div className="flex gap-1">
        <Button
          icon="pi pi-pencil"
          size="small"
          text
          onClick={() => handleStartEdit(comment)}
        />
        <Button
          icon="pi pi-trash"
          size="small"
          severity="danger"
          text
          onClick={() => handleConfirmDelete(comment)}
        />
      </div>
    );
  };

  return (
    <>
      <Dialog
        header="Komentarze"
        visible={rowData !== null}
        onHide={handleHide}
        breakpoints={{ "2000px": "70vw", "960px": "75vw", "641px": "95vw" }}
        style={{ width: "70vw" }}
      >
        <div className="mb-4 field">
          <label>
            {editingCommentId ? "Edytuj komentarz" : "Dodaj komentarz"}:
          </label>
          <InputTextarea
            value={newCommentText}
            onChange={(e) => setNewCommentText(e.target.value)}
            rows={3}
            className="w-full"
            placeholder="Wpisz komentarz..."
            autoResize
          />
          <div className="toolbar-first-row">
            <div className="toolbar-row--inner-container">
              <Button
                label={editingCommentId ? "Ratować" : "Dodaj"}
                icon={`pi ${editingCommentId ? "pi-save" : "pi-plus"}`}
                onClick={() => {
                  if (editingCommentId) {
                    void handleSaveEdit(editingCommentId);
                  } else {
                    void handleCreateComment();
                  }
                }}
                disabled={!newCommentText.trim() || isCreating || isUpdating}
                loading={isCreating || isUpdating}
              />
              {editingCommentId ? (
                <Button
                  label="Anulować"
                  icon="pi pi-cancel"
                  onClick={() => {
                    handleCancelEdit();
                  }}
                  disabled={!newCommentText.trim() || isCreating || isUpdating}
                  loading={isCreating || isUpdating}
                />
              ) : null}
            </div>
          </div>
        </div>

        <DataTable
          value={comments?.items ?? []}
          lazy
          paginator
          loading={isFetching}
          emptyMessage="Brak komentarzy"
          rows={COMMENTS_PER_PAGE}
          first={commentsPage * COMMENTS_PER_PAGE}
          totalRecords={comments?.total ?? 0}
          onPage={onPage}
          sortOrder={-1}
          sortField="created_at"
          size="small"
        >
          <Column
            header="Data"
            field="created_at"
            body={createdAtBody}
            style={{ width: "12rem" }}
          />
          <Column
            header="Utworzono przez"
            field="created_by"
            body={createdByBody}
            style={{ width: "12rem" }}
          />
          <Column header="Komentarz" field="text" body={textBody} />
          <Column
            header="Edytowany"
            field="updated_at"
            body={updatedAtBody}
            style={{ width: "15rem" }}
          />
          <Column header="Akcje" body={actionsBody} style={{ width: "8rem" }} />
        </DataTable>
      </Dialog>

      <ConfirmDialog
        visible={deleteConfirmVisible}
        onHide={() => setDeleteConfirmVisible(false)}
        message="Czy na pewno chcesz usunąć ten komentarz?"
        header="Potwierdzenie"
        icon="pi pi-exclamation-triangle"
        accept={() => {
          void handleDeleteComment();
        }}
        reject={() => {
          setDeleteConfirmVisible(false);
          setCommentToDelete(null);
        }}
        acceptLabel="Tak"
        rejectLabel="Nie"
        acceptClassName="p-button-danger"
      />
    </>
  );
}
